import { Helmet } from "react-helmet-async";

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - TodoTomorrow</title>
        <meta name="description" content="TodoTomorrow's privacy policy. Learn how we protect your data and respect your privacy when using our task management app." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href="https://www.todotomorrow.com/privacy" />
        <meta property="og:title" content="Privacy Policy - TodoTomorrow" />
        <meta property="og:description" content="TodoTomorrow's privacy policy. Learn how we protect your data and respect your privacy when using our task management app." />
        <meta property="og:url" content="https://www.todotomorrow.com/privacy" />
        <meta name="twitter:title" content="Privacy Policy - TodoTomorrow" />
        <meta name="twitter:description" content="TodoTomorrow's privacy policy. Learn how we protect your data and respect your privacy when using our task management app." />
      </Helmet>
      <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="container mx-auto max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-8">
          Privacy Policy
        </h1>
        
        <div className="prose prose-lg max-w-none space-y-6">
          <p className="text-muted-foreground">
            <strong>Effective Date: November 4, 2025</strong>
          </p>
          
          <p className="text-muted-foreground">
            TodoTomorrow ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Service").
          </p>
          
          <p className="text-muted-foreground">
            By using TodoTomorrow, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our Service.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            1. Information We Collect
          </h2>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            1.1 Information You Provide
          </h3>
          
          <p className="text-muted-foreground">
            We collect information that you voluntarily provide when using our Service:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li><strong>Email Address:</strong> Required for account creation, authentication, and email delivery of your task list.</li>
            <li><strong>Task Content:</strong> The todo items you create within the app, including task text, optional due dates, and priority levels.</li>
            <li><strong>User Preferences:</strong> Your chosen email delivery time, timezone, and workflow mode (Fresh Start or Carry Over).</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            1.2 Automatically Collected Information
          </h3>
          
          <p className="text-muted-foreground">
            When you access our Service, we may automatically collect:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li><strong>Device Information:</strong> Device type, operating system, app version, and unique device identifiers.</li>
            <li><strong>Usage Data:</strong> App interactions, features used, crash reports, and performance data.</li>
            <li><strong>Log Data:</strong> IP address, access times, and pages viewed (for website only).</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            2. How We Use Your Information
          </h2>
          
          <p className="text-muted-foreground">
            We use the collected information for the following purposes:
          </p>
          
          <ol className="list-decimal pl-6 text-muted-foreground space-y-2">
            <li><strong>Service Delivery:</strong> To provide core functionality, including task storage, synchronization, and automated email delivery.</li>
            <li><strong>Account Management:</strong> To create and manage your account and authenticate access.</li>
            <li><strong>Communication:</strong> To send your daily task emails, transactional messages, and customer support responses.</li>
            <li><strong>Service Improvement:</strong> To analyze usage patterns, fix bugs, enhance features, and improve overall user experience.</li>
            <li><strong>Legal Compliance:</strong> To comply with legal obligations, enforce our Terms of Service, and protect our rights.</li>
            <li><strong>Security:</strong> To detect and prevent fraud, unauthorized access, and other security threats.</li>
          </ol>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            3. Data Sharing and Disclosure
          </h2>
          
          <p className="text-muted-foreground">
            We do not sell, rent, or trade your personal information. We may share your information in the following limited circumstances:
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            3.1 Service Providers
          </h3>
          
          <p className="text-muted-foreground">
            We work with trusted third-party service providers who assist in operating our Service:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li><strong>Supabase:</strong> Cloud database and authentication services (data stored in secure, GDPR-compliant data centers).</li>
            <li><strong>Resend:</strong> Email delivery service for sending your daily task lists.</li>
          </ul>
          
          <p className="text-muted-foreground">
            These providers have access only to information necessary to perform their functions and are contractually obligated to maintain confidentiality and security.
          </p>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            3.2 Legal Requirements
          </h3>
          
          <p className="text-muted-foreground">
            We may disclose your information if required to do so by law or in response to:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Valid legal processes (subpoenas, court orders, government requests)</li>
            <li>Enforcement of our Terms of Service or investigation of violations</li>
            <li>Protection of the rights, property, or safety of TodoTomorrow, our users, or others</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">
            3.3 Business Transfers
          </h3>
          
          <p className="text-muted-foreground">
            In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity. We will notify you via email and/or prominent notice in the app before your information becomes subject to a different privacy policy.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            4. Data Retention
          </h2>
          
          <p className="text-muted-foreground">
            We retain your information for as long as necessary to provide the Service and fulfill the purposes outlined in this Privacy Policy:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li><strong>Active Accounts:</strong> Task data and account information are retained while your account is active.</li>
            <li><strong>Deleted Accounts:</strong> Upon account deletion, your personal data is permanently removed within 30 days. Some information may be retained in backup systems for up to 90 days.</li>
            <li><strong>Legal Obligations:</strong> Certain information may be retained longer if required by law or for legitimate business purposes (e.g., fraud prevention, financial records).</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            5. Data Security
          </h2>
          
          <p className="text-muted-foreground">
            We implement industry-standard security measures to protect your information:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li><strong>Encryption:</strong> Data is encrypted in transit using SSL/TLS and at rest in our databases.</li>
            <li><strong>Authentication:</strong> Secure authentication via Supabase Auth with magic link login.</li>
            <li><strong>Access Controls:</strong> Limited access to personal data on a need-to-know basis.</li>
            <li><strong>Infrastructure:</strong> Hosting on secure, regularly monitored servers with automatic security updates.</li>
          </ul>
          
          <p className="text-muted-foreground">
            While we strive to protect your information, no method of transmission or storage is 100% secure. We cannot guarantee absolute security but will notify you of any data breaches as required by law.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            6. Your Privacy Rights
          </h2>
          
          <p className="text-muted-foreground">
            Depending on your location, you may have the following rights regarding your personal information:
          </p>
          
          <ol className="list-decimal pl-6 text-muted-foreground space-y-2">
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information through your account settings.</li>
            <li><strong>Deletion:</strong> Request deletion of your account and associated data (available in Settings).</li>
            <li><strong>Portability:</strong> Request an export of your task data in a machine-readable format.</li>
            <li><strong>Objection:</strong> Object to certain processing of your information (subject to legal limitations).</li>
            <li><strong>Withdrawal of Consent:</strong> Withdraw consent for data processing where consent is the legal basis.</li>
          </ol>
          
          <p className="text-muted-foreground">
            To exercise these rights, please contact us at info@todotomorrow.com. We will respond within 30 days of receiving your request.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            7. International Data Transfers
          </h2>
          
          <p className="text-muted-foreground">
            TodoTomorrow is based in the United States, and our service providers may be located in various countries. By using our Service, you consent to the transfer of your information to countries outside your country of residence, including the United States, which may have different data protection laws. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable laws, including GDPR requirements for EU users.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            8. Children's Privacy
          </h2>
          
          <p className="text-muted-foreground">
            TodoTomorrow is not intended for children under the age of 13 (or 16 in the European Economic Area). We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal information, we will take steps to delete such information. If you believe we have collected information from a child, please contact us immediately at info@todotomorrow.com.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            9. Third-Party Links and Services
          </h2>
          
          <p className="text-muted-foreground">
            Our Service may contain links to third-party websites or services that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services. We encourage you to review the privacy policies of any third-party sites you visit.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            10. California Privacy Rights
          </h2>
          
          <p className="text-muted-foreground">
            If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Right to know what personal information is collected, used, shared, or sold</li>
            <li>Right to delete personal information held by us</li>
            <li>Right to opt-out of the sale of personal information (Note: We do not sell personal information)</li>
            <li>Right to non-discrimination for exercising your CCPA rights</li>
          </ul>
          
          <p className="text-muted-foreground">
            To exercise these rights, contact us at info@todotomorrow.com with the subject line "California Privacy Request."
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            11. GDPR Compliance (EU Users)
          </h2>
          
          <p className="text-muted-foreground">
            If you are located in the European Economic Area (EEA), we process your personal data under the following legal bases:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li><strong>Contractual Necessity:</strong> Processing necessary to provide the Service you requested.</li>
            <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate business interests (e.g., service improvement, fraud prevention).</li>
            <li><strong>Consent:</strong> Where you have given explicit consent for specific processing activities.</li>
            <li><strong>Legal Obligation:</strong> Where processing is required to comply with legal obligations.</li>
          </ul>
          
          <p className="text-muted-foreground">
            You have the right to lodge a complaint with your local data protection authority if you believe we have not complied with GDPR requirements.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            12. Changes to This Privacy Policy
          </h2>
          
          <p className="text-muted-foreground">
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will notify you by:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Posting the updated policy in the app and on our website</li>
            <li>Sending an email notification to your registered email address</li>
          </ul>
          
          <p className="text-muted-foreground">
            The "Effective Date" at the top of this policy indicates when it was last updated. Your continued use of the Service after changes become effective constitutes acceptance of the updated Privacy Policy.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            13. Account and Data Deletion
          </h2>
          
          <p className="text-muted-foreground">
            You can delete your account and all associated data at any time:
          </p>
          
          <ol className="list-decimal pl-6 text-muted-foreground space-y-2">
            <li>Open the TodoTomorrow app</li>
            <li>Go to Settings &gt; Account</li>
            <li>Tap "Delete Account"</li>
            <li>Confirm deletion</li>
          </ol>
          
          <p className="text-muted-foreground">
            When you delete your account, we permanently delete:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li>Your email address</li>
            <li>All tasks and task history</li>
            <li>Your preferences and settings</li>
            <li>All other personal data</li>
          </ul>
          
          <p className="text-muted-foreground">
            <strong>Data deletion is immediate and cannot be undone.</strong> Some information may remain in backup systems for up to 90 days, after which it is permanently removed.
          </p>
          
          <p className="text-muted-foreground">
            If you need assistance with account deletion, contact us at info@todotomorrow.com
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            14. Contact Us
          </h2>
          
          <p className="text-muted-foreground">
            If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>
          
          <p className="text-muted-foreground">
            <strong>Email:</strong> info@todotomorrow.com<br />
            <strong>Website:</strong> https://todotomorrow.com/privacy<br />
            <strong>Response Time:</strong> We aim to respond to all inquiries within 48 hours.
          </p>
          
          <h2 className="text-2xl font-semibold text-foreground mt-12 mb-4">
            15. Android Permissions
          </h2>
          
          <p className="text-muted-foreground">
            TodoTomorrow requests the following Android permissions to provide functionality:
          </p>
          
          <ul className="list-disc pl-6 text-muted-foreground space-y-2">
            <li><strong>Internet Access (INTERNET):</strong> Required to sync your tasks to the cloud and send daily emails.</li>
            <li><strong>Network State (ACCESS_NETWORK_STATE):</strong> Used to detect when you're online to enable background synchronization.</li>
            <li><strong>Notifications (POST_NOTIFICATIONS):</strong> Used to send important service notifications and reminders (optional, can be disabled in device settings).</li>
            <li><strong>Wake Lock (WAKE_LOCK):</strong> Allows the app to keep your device awake briefly to ensure scheduled email delivery occurs on time.</li>
          </ul>
          
          <p className="text-muted-foreground">
            We do not request access to sensitive permissions such as Camera, Microphone, Location, Contacts, Storage, SMS, or Phone Calls. All permissions requested are necessary for core app functionality and email delivery.
          </p>
          
          <p className="text-muted-foreground italic mt-8">
            Thank you for trusting TodoTomorrow with your data. We are committed to protecting your privacy and providing a secure, simple task management experience.
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default Privacy;
